import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import api, { createListing } from '../services/api';
import { 
  ArrowRight, ArrowLeft, Check, X, Home, Users, 
  Briefcase, Euro, Camera, MapPin, Ruler, Sofa,
  GraduationCap, Globe2, Heart, Sparkles, Upload,
  ChevronUp, Building2, Calendar, Loader2, ImagePlus,
  Dog, FileCheck, User, Search
} from 'lucide-react';

const TENANT_TYPES = [
  { id: 'student', label: 'Student', icon: GraduationCap, desc: 'HBO/WO studenten' },
  { id: 'starter', label: 'Starter', icon: Briefcase, desc: 'Net afgestudeerd, eerste baan' },
  { id: 'professional', label: 'Professional', icon: Building2, desc: 'Werkend met vast inkomen' },
  { id: 'expat', label: 'Expat', icon: Globe2, desc: 'Internationaal talent' },
  { id: 'koppel', label: 'Koppel', icon: Heart, desc: 'Samen op zoek' },
  { id: 'gezin', label: 'Gezin', icon: Users, desc: 'Met kinderen' },
  { id: 'any', label: 'Iedereen', icon: Users, desc: 'Geen voorkeur' },
];

const AGE_RANGES = [
  { id: '18-25', label: '18-25 jaar', desc: 'Studenten & starters' },
  { id: '25-35', label: '25-35 jaar', desc: 'Young professionals' },
  { id: '35-50', label: '35-50 jaar', desc: 'Ervaren professionals' },
  { id: '50+', label: '50+ jaar', desc: 'Senioren' },
  { id: 'any', label: 'Alle leeftijden', desc: 'Geen voorkeur' },
];

const GENDER_OPTIONS = [
  { id: 'man', label: 'Man' },
  { id: 'vrouw', label: 'Vrouw' },
  { id: 'any', label: 'Geen voorkeur' },
];

const LANGUAGE_OPTIONS = [
  { id: 'nl', label: 'Nederlands', emoji: '🇳🇱' },
  { id: 'en', label: 'Engels', emoji: '🇬🇧' },
  { id: 'de', label: 'Duits', emoji: '🇩🇪' },
  { id: 'fr', label: 'Frans', emoji: '🇫🇷' },
  { id: 'es', label: 'Spaans', emoji: '🇪🇸' },
  { id: 'any', label: 'Geen voorkeur', emoji: '🌍' },
];

const CITIES = [
  { id: 'amsterdam', name: 'Amsterdam', popular: true },
  { id: 'rotterdam', name: 'Rotterdam', popular: true },
  { id: 'utrecht', name: 'Utrecht', popular: true },
  { id: 'den-haag', name: 'Den Haag', popular: true },
  { id: 'eindhoven', name: 'Eindhoven', popular: false },
  { id: 'groningen', name: 'Groningen', popular: false },
  { id: 'tilburg', name: 'Tilburg', popular: false },
  { id: 'almere', name: 'Almere', popular: false },
];

const PROPERTY_TYPES = [
  { id: 'kamer', label: 'Kamer', desc: 'Privé kamer, gedeelde voorzieningen' },
  { id: 'studio', label: 'Studio', desc: 'Zelfstandig, alles-in-één' },
  { id: 'appartement', label: 'Appartement', desc: 'Volledige woning' },
  { id: 'eengezinswoning', label: 'Eengezinswoning', desc: 'Voor gezinnen' },
];

// Rental duration options (for landlord to specify minimum rental period)
const RENTAL_DURATIONS = [
  { id: 'short_1_3', label: '1-3 maanden', desc: 'Short stay' },
  { id: 'medium_3_6', label: '3-6 maanden', desc: 'Tijdelijk' },
  { id: 'medium_6_12', label: '6-12 maanden', desc: 'Middellang' },
  { id: 'long_1_year', label: '1 jaar+', desc: 'Langdurig' },
  { id: 'any', label: 'Flexibel', desc: 'Geen minimum' },
];

// Property Features - Comprehensive list
const PROPERTY_FEATURES = {
  outdoor: {
    title: 'Oppervlakte & Indeling',
    titleEn: 'Surface & Layout',
    features: [
      { id: 'balcony', label: 'Balkon/terras', labelEn: 'Balcony/terrace', hasSize: true },
      { id: 'garden', label: 'Tuin', labelEn: 'Garden', hasSize: true },
      { id: 'storage', label: 'Berging/schuur', labelEn: 'Storage/shed' },
    ]
  },
  shared: {
    title: 'Gedeeld of Eigen',
    titleEn: 'Shared or Private',
    features: [
      { id: 'kitchen', label: 'Keuken', labelEn: 'Kitchen', options: ['eigen', 'gedeeld'] },
      { id: 'shower', label: 'Douche', labelEn: 'Shower', options: ['eigen', 'gedeeld'] },
      { id: 'toilet', label: 'Toilet', labelEn: 'Toilet', options: ['eigen', 'gedeeld'] },
      { id: 'livingroom', label: 'Woonkamer', labelEn: 'Living room', options: ['eigen', 'gedeeld'] },
    ]
  },
  energy: {
    title: 'Energie',
    titleEn: 'Energy',
    features: [
      { id: 'energyLabel', label: 'Energielabel', labelEn: 'Energy label', options: ['A++++', 'A+++', 'A++', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'] },
      { id: 'solarPanels', label: 'Zonnepanelen', labelEn: 'Solar panels' },
    ]
  },
  amenities: {
    title: 'Voorzieningen',
    titleEn: 'Amenities',
    features: [
      { id: 'furnished', label: 'Gemeubileerd', labelEn: 'Furnished', options: ['ja', 'nee', 'gedeeltelijk'] },
      { id: 'elevator', label: 'Lift aanwezig', labelEn: 'Elevator' },
      { id: 'washingConnection', label: 'Wasmachine aansluiting', labelEn: 'Washing machine connection' },
      { id: 'dryerConnection', label: 'Droger aansluiting', labelEn: 'Dryer connection' },
      { id: 'dishwasher', label: 'Vaatwasser', labelEn: 'Dishwasher' },
      { id: 'kitchenType', label: 'Keuken', labelEn: 'Kitchen', options: ['luxe', 'standaard', 'basis'] },
      { id: 'internetIncluded', label: 'Internet inbegrepen', labelEn: 'Internet included' },
      { id: 'smokingAllowed', label: 'Roken toegestaan', labelEn: 'Smoking allowed' },
    ]
  },
  bathroom: {
    title: 'Badkamer',
    titleEn: 'Bathroom',
    features: [
      { id: 'bathtub', label: 'Bad', labelEn: 'Bathtub' },
      { id: 'showerBathroom', label: 'Douche', labelEn: 'Shower' },
      { id: 'sink', label: 'Wastafel', labelEn: 'Sink' },
      { id: 'toiletInBathroom', label: 'Toilet in badkamer', labelEn: 'Toilet in bathroom' },
    ]
  },
  parking: {
    title: 'Parkeren',
    titleEn: 'Parking',
    features: [
      { id: 'parkingIncluded', label: 'Parkeerplaats inclusief', labelEn: 'Parking included' },
      { id: 'parkingAvailable', label: 'Parkeerplaats beschikbaar (betaald)', labelEn: 'Parking available (paid)' },
      { id: 'parkingPermitNeeded', label: 'Parkeervergunning nodig', labelEn: 'Parking permit needed' },
      { id: 'noParking', label: 'Geen parkeermogelijkheid', labelEn: 'No parking' },
    ]
  },
  location: {
    title: 'Buurt & Locatie',
    titleEn: 'Neighborhood & Location',
    features: [
      { id: 'publicTransport', label: 'Goede OV verbinding', labelEn: 'Good public transport' },
      { id: 'supermarketNearby', label: 'Supermarkt op loopafstand', labelEn: 'Supermarket nearby' },
      { id: 'schoolNearby', label: 'School op loopafstand', labelEn: 'School nearby' },
      { id: 'neighborhoodType', label: 'Woonwijk type', labelEn: 'Neighborhood type', options: ['rustig', 'levendig', 'centrum'] },
    ]
  },
  extra: {
    title: 'Extra Features',
    titleEn: 'Extra Features',
    features: [
      { id: 'airco', label: 'Airco', labelEn: 'Air conditioning' },
      { id: 'smartHome', label: 'Smart home features', labelEn: 'Smart home features' },
      { id: 'alarmSystem', label: 'Alarm systeem', labelEn: 'Alarm system' },
      { id: 'videoIntercom', label: 'Video intercom', labelEn: 'Video intercom' },
      { id: 'bikeStorage', label: 'Fietsenberging', labelEn: 'Bike storage' },
    ]
  }
};

const LandlordOnboardingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Form data
  const [preferredTenants, setPreferredTenants] = useState([]);
  const [preferredAgeRange, setPreferredAgeRange] = useState('any');
  const [preferredGender, setPreferredGender] = useState('any');
  const [preferredLanguage, setPreferredLanguage] = useState('any'); // NEW: Language preference
  const [minIncome, setMinIncome] = useState('');
  
  // NEW: Additional preferences for landlords
  const [maxPersons, setMaxPersons] = useState(2);
  const [petsAllowed, setPetsAllowed] = useState(null);
  const [registrationAllowed, setRegistrationAllowed] = useState(null);
  const [minRentalDuration, setMinRentalDuration] = useState('any');
  
  const [propertyData, setPropertyData] = useState({
    type: '',
    city: '',
    neighborhood: '',
    postalCode: '',
    streetAddress: '',
    price: '',
    size: '',
    furnished: null,
    semifurnished: false, // NEW: Gestoffeerd option
    available: '',
    availableUntil: 'indefinite',
    title: '',
    description: '',
    // NEW: Utility and service costs
    utilitiesIncluded: null, // true = inclusief, false = exclusief GWL
    utilitiesCost: '', // Monthly GWL cost if excluded
    serviceCost: '', // Optional extra service costs
    deposit: '', // NEW: Borg/deposit amount
  });
  
  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  
  // NEW: Property features state
  const [propertyFeatures, setPropertyFeatures] = useState({
    // Outdoor - with optional size
    balcony: false,
    balconySize: '',
    garden: false,
    gardenSize: '',
    storage: false,
    // Shared/Private options
    kitchen: '', // 'eigen' or 'gedeeld'
    shower: '',
    toilet: '',
    livingroom: '',
    // Energy
    energyLabel: '',
    solarPanels: false,
    // Amenities
    furnished: '', // 'ja', 'nee', 'gedeeltelijk'
    elevator: false,
    washingConnection: false,
    dryerConnection: false,
    dishwasher: false,
    kitchenType: '', // 'luxe', 'standaard', 'basis'
    internetIncluded: false,
    smokingAllowed: false,
    // Bathroom
    bathtub: false,
    showerBathroom: false,
    sink: false,
    toiletInBathroom: false,
    // Parking
    parkingIncluded: false,
    parkingAvailable: false,
    parkingPermitNeeded: false,
    noParking: false,
    // Location
    publicTransport: false,
    supermarketNearby: false,
    schoolNearby: false,
    neighborhoodType: '', // 'rustig', 'levendig', 'centrum'
    // Extra
    airco: false,
    smartHome: false,
    alarmSystem: false,
    videoIntercom: false,
    bikeStorage: false,
  });

  const TOTAL_STEPS = 8; // Updated from 7 to 8 (added features step)

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?redirect=/verhuurder/onboarding');
      return;
    }
    // Only landlords can access this page
    if (user?.userType === 'huurder') {
      toast({
        title: "Geen toegang",
        description: "Alleen verhuurders kunnen woningen plaatsen. Maak een verhuurder account aan.",
        variant: "destructive"
      });
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, authLoading, navigate, user, toast]);

  const toggleTenantType = (id) => {
    if (id === 'any') {
      setPreferredTenants(['any']);
    } else {
      setPreferredTenants(prev => {
        const filtered = prev.filter(t => t !== 'any');
        if (filtered.includes(id)) {
          return filtered.filter(t => t !== id);
        }
        return [...filtered, id];
      });
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 4 * 1024 * 1024) continue;

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setUploadedImages(prev => [...prev, response.data.url]);
      } catch (error) {
        console.error('Upload error:', error);
      }
    }
    
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };
  
  // Address autocomplete using PDOK Locatieserver (Dutch government API)
  const searchAddress = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setLoadingAddress(true);
    try {
      const response = await fetch(
        `https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?q=${encodeURIComponent(query)}&fq=type:adres&rows=5`
      );
      const data = await response.json();
      
      if (data.response?.docs) {
        const suggestions = data.response.docs.map(doc => {
          // Extract city and street from weergavenaam (format: "Straatnaam 123, 1234AB Stad")
          const displayName = doc.weergavenaam || '';
          const parts = displayName.split(',');
          
          let extractedCity = '';
          let extractedStreet = '';
          let extractedPostcode = '';
          
          if (parts.length >= 2) {
            // First part is street + house number (e.g., "Prinsengracht 1017")
            extractedStreet = parts[0].trim();
            
            // Last part contains postcode and city (e.g., "1017KN Amsterdam")
            const lastPart = parts[parts.length - 1].trim();
            const lastPartWords = lastPart.split(' ');
            
            // First word is usually the postcode
            if (lastPartWords.length > 0 && /\d/.test(lastPartWords[0])) {
              extractedPostcode = lastPartWords[0];
              extractedCity = lastPartWords.slice(1).join(' ');
            } else {
              extractedCity = lastPart;
            }
          }
          
          return {
            id: doc.id,
            display: displayName,
            postcode: doc.postcode || extractedPostcode || '',
            city: doc.woonplaatsnaam || extractedCity || '',
            street: doc.straatnaam || extractedStreet || '',
            houseNumber: doc.huisnummer || '',
            neighborhood: doc.wijknaam || doc.buurtnaam || ''
          };
        });
        setAddressSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Address search error:', error);
      setAddressSuggestions([]);
    } finally {
      setLoadingAddress(false);
    }
  };
  
  const selectAddress = (suggestion) => {
    setPropertyData(prev => ({
      ...prev,
      postalCode: suggestion.postcode || '',
      city: suggestion.city || prev.city,
      neighborhood: suggestion.neighborhood || suggestion.street || '',
      streetAddress: suggestion.street ? `${suggestion.street} ${suggestion.houseNumber || ''}`.trim() : ''
    }));
    setAddressQuery(suggestion.display);
    setAddressSuggestions([]);
  };

  const handleSubmit = async () => {
    if (!propertyData.title || !propertyData.price) {
      toast({ title: language === 'nl' ? 'Vul alle velden in' : 'Fill in all fields', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save landlord preferences with new fields
      await api.post('/landlord/preferences', {
        preferredTenantTypes: preferredTenants,
        preferredAgeRange: preferredAgeRange,
        preferredGender: preferredGender,
        minIncome: minIncome ? parseFloat(minIncome) : null,
        // NEW: Additional preferences
        maxPersons: maxPersons,
        petsAllowed: petsAllowed,
        registrationAllowed: registrationAllowed,
        minRentalDuration: minRentalDuration,
      });

      // Create listing with all preferences
      const features = [];
      if (propertyData.furnished) features.push('Gemeubileerd');
      if (propertyData.semifurnished) features.push('Gestoffeerd');
      
      const listingData = {
        title: propertyData.title,
        type: propertyData.type,
        city: propertyData.city,
        neighborhood: propertyData.neighborhood || 'Centrum',
        postalCode: propertyData.postalCode || '',
        streetAddress: propertyData.streetAddress || '',
        price: parseFloat(propertyData.price),
        size: parseFloat(propertyData.size) || 0,
        available: propertyData.available || new Date().toISOString().split('T')[0],
        availableUntil: propertyData.availableUntil || 'indefinite',
        furnished: propertyData.furnished === true,
        semifurnished: propertyData.semifurnished === true,
        images: uploadedImages,
        description: propertyData.description,
        features: features,
        landlordName: user?.name || 'Verhuurder',
        landlordEmail: user?.email,
        landlordPhone: '',
        // Landlord preferences for this listing
        preferredTenantTypes: preferredTenants,
        preferredAgeRange: preferredAgeRange,
        preferredGender: preferredGender,
        preferredLanguage: preferredLanguage,
        // NEW: Additional listing preferences
        maxPersons: maxPersons,
        petsAllowed: petsAllowed,
        registrationAllowed: registrationAllowed,
        minRentalDuration: minRentalDuration,
        // NEW: Utility and service costs
        utilitiesIncluded: propertyData.utilitiesIncluded,
        utilitiesCost: propertyData.utilitiesCost ? parseFloat(propertyData.utilitiesCost) : null,
        serviceCost: propertyData.serviceCost ? parseFloat(propertyData.serviceCost) : null,
        deposit: propertyData.deposit ? parseFloat(propertyData.deposit) : null,
        // NEW: Property features
        propertyFeatures: propertyFeatures,
      };

      const newListing = await createListing(listingData);
      const newListingId = newListing?.id;
      
      toast({ 
        title: '🎉 Woning geplaatst!', 
        description: language === 'nl' ? 'Boost nu je advertentie voor meer reacties!' : 'Boost your ad now for more responses!'
      });
      
      // Redirect to boost page with listing ID
      if (newListingId) {
        navigate(`/verhuurder/boost/${newListingId}?from=onboarding`);
      } else {
        navigate('/verhuurder/dashboard');
      }
    } catch (error) {
      toast({ 
        title: 'Er ging iets mis', 
        description: error.response?.data?.detail || 'Probeer opnieuw',
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return preferredTenants.length > 0;
      case 2: return true; // House rules all have default values or are optional
      case 3: return !!propertyData.type;
      case 4: return !!propertyData.city && !!propertyData.price && !!propertyData.size; // Size now required
      case 5: return (propertyData.furnished !== null || propertyData.semifurnished) && !!propertyData.available && propertyData.utilitiesIncluded !== null; // Available and utilities required
      case 6: return true; // Property features - all optional
      case 7: return uploadedImages.length >= 1;
      case 8: return !!propertyData.title && !!propertyData.description;
      default: return true;
    }
  };

  // Loading
  if (authLoading) {
    return (
      <div className="fixed inset-0 bg-[#F7F7F7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-[#1F3A5F] rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Home className="w-10 h-10 text-white" />
          </div>
          <p className="text-[#9E9E9E]">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F7F7F7] overflow-hidden" data-testid="landlord-onboarding">
      <Toaster />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-30 bg-[#F7F7F7]/90 backdrop-blur-sm border-b border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}>
            {step > 1 ? (
              <ArrowLeft className="w-6 h-6 text-[#2A2A2A]" />
            ) : (
              <X className="w-6 h-6 text-[#2A2A2A]" />
            )}
          </button>
          
          <SwipeMatchLogo size="small" variant="color" />
          
          <span className="text-sm font-medium text-[#9E9E9E]">{step}/{TOTAL_STEPS}</span>
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-[#E5E5E5]">
          <div 
            className="h-full bg-[#2E7D32] transition-all duration-500"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="h-full pt-16 pb-24 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4 py-6">
          
          {/* Step 1: Tenant Preferences */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Sparkles className="w-4 h-4" />
                  Swipe je woning voor Verhuurders
                </div>
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  Jouw ideale huurder
                </h1>
                <p className="text-[#9E9E9E]">
                  Geef aan welk type huurder je zoekt
                </p>
              </div>

              {/* Tenant Type Selection */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">Type huurder</label>
                <div className="grid grid-cols-2 gap-3">
                  {TENANT_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = preferredTenants.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => toggleTenantType(type.id)}
                        className={`p-4 rounded-2xl border-2 text-left transition-all relative ${
                          isSelected
                            ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                            : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                          isSelected ? 'bg-[#2E7D32] text-white' : 'bg-[#F7F7F7] text-[#9E9E9E]'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <h3 className="font-bold text-[#2A2A2A]">{type.label}</h3>
                        <p className="text-xs text-[#9E9E9E] mt-1">{type.desc}</p>
                        {isSelected && (
                          <div className="absolute top-3 right-3 w-5 h-5 bg-[#2E7D32] rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Age Range Selection */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">Leeftijd voorkeur</label>
                <div className="grid grid-cols-2 gap-2">
                  {AGE_RANGES.map((range) => (
                    <button
                      key={range.id}
                      onClick={() => setPreferredAgeRange(range.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        preferredAgeRange === range.id
                          ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                          : 'border-[#E5E5E5] bg-white'
                      }`}
                    >
                      <span className="font-medium text-[#2A2A2A]">{range.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Gender Selection */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">Geslacht voorkeur</label>
                <div className="grid grid-cols-3 gap-2">
                  {GENDER_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setPreferredGender(option.id)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        preferredGender === option.id
                          ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                          : 'border-[#E5E5E5] bg-white'
                      }`}
                    >
                      <span className="font-medium text-[#2A2A2A]">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Language Preference */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">Taal voorkeur</label>
                <div className="grid grid-cols-2 gap-2">
                  {LANGUAGE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setPreferredLanguage(option.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        preferredLanguage === option.id
                          ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                          : 'border-[#E5E5E5] bg-white'
                      }`}
                    >
                      <span className="text-lg mr-2">{option.emoji}</span>
                      <span className="font-medium text-[#2A2A2A]">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-[#E5E5E5]">
                <p className="text-sm text-[#9E9E9E]">
                  <span className="font-bold text-[#2A2A2A]">82%</span> van de verhuurders vindt binnen 2 weken een geschikte huurder via Swipe je woning
                </p>
              </div>
            </div>
          )}

          {/* Step 2: House Rules - NEW */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  {language === 'nl' ? 'Huisregels' : 'House Rules'}
                </h1>
                <p className="text-[#9E9E9E]">
                  {language === 'nl' ? 'Belangrijke voorwaarden voor huurders' : 'Important conditions for tenants'}
                </p>
              </div>

              {/* Max Persons */}
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-4 block flex items-center gap-2">
                  <User className="w-5 h-5 text-[#2E7D32]" />
                  {language === 'nl' ? 'Maximum aantal personen' : 'Maximum number of persons'}
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setMaxPersons(prev => Math.max(1, prev - 1))}
                    className="w-12 h-12 rounded-full border-2 border-[#E5E5E5] flex items-center justify-center hover:border-[#2E7D32] transition-colors text-xl font-bold"
                  >
                    -
                  </button>
                  <div className="flex-1 text-center">
                    <span className="text-4xl font-black text-[#2A2A2A]">{maxPersons}</span>
                    <p className="text-sm text-[#9E9E9E]">
                      {maxPersons === 1 ? (language === 'nl' ? 'persoon' : 'person') : (language === 'nl' ? 'personen' : 'persons')}
                    </p>
                  </div>
                  <button
                    onClick={() => setMaxPersons(prev => Math.min(10, prev + 1))}
                    className="w-12 h-12 rounded-full border-2 border-[#E5E5E5] flex items-center justify-center hover:border-[#2E7D32] transition-colors text-xl font-bold"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Pets Allowed */}
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-4 block flex items-center gap-2">
                  <Dog className="w-5 h-5 text-[#2E7D32]" />
                  {language === 'nl' ? 'Huisdieren toegestaan?' : 'Pets allowed?'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPetsAllowed(true)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      petsAllowed === true
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-3xl mb-2 block">🐕</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Ja, toegestaan' : 'Yes, allowed'}</span>
                  </button>
                  <button
                    onClick={() => setPetsAllowed(false)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      petsAllowed === false
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-3xl mb-2 block">🚫</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Nee, niet toegestaan' : 'No, not allowed'}</span>
                  </button>
                </div>
              </div>

              {/* Registration Allowed */}
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-[#2E7D32]" />
                  {language === 'nl' ? 'Inschrijving op adres mogelijk?' : 'Registration at address possible?'}
                </label>
                <p className="text-xs text-[#9E9E9E] mb-4">
                  {language === 'nl' 
                    ? 'Kan de huurder zich inschrijven bij de gemeente op dit adres?' 
                    : 'Can the tenant register at the municipality at this address?'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setRegistrationAllowed(true)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      registrationAllowed === true
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-3xl mb-2 block">📋</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Ja, mogelijk' : 'Yes, possible'}</span>
                  </button>
                  <button
                    onClick={() => setRegistrationAllowed(false)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      registrationAllowed === false
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-3xl mb-2 block">✗</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Nee, niet mogelijk' : 'No, not possible'}</span>
                  </button>
                </div>
              </div>

              {/* Minimum Rental Duration */}
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-4 block flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#2E7D32]" />
                  {language === 'nl' ? 'Minimale huurtermijn' : 'Minimum rental period'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {RENTAL_DURATIONS.map((duration) => (
                    <button
                      key={duration.id}
                      onClick={() => setMinRentalDuration(duration.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        minRentalDuration === duration.id
                          ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                          : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                      }`}
                    >
                      <span className="font-bold text-sm text-[#2A2A2A]">{duration.label}</span>
                      <p className="text-xs text-[#9E9E9E]">{duration.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Property Type */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  Wat verhuur je?
                </h1>
                <p className="text-[#9E9E9E]">
                  Selecteer het type woonruimte
                </p>
              </div>

              <div className="space-y-3">
                {PROPERTY_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setPropertyData(prev => ({ ...prev, type: type.id }))}
                    className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center justify-between ${
                      propertyData.type === type.id
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <div>
                      <h3 className="font-bold text-[#2A2A2A] text-lg">{type.label}</h3>
                      <p className="text-sm text-[#9E9E9E]">{type.desc}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      propertyData.type === type.id 
                        ? 'border-[#2E7D32] bg-[#2E7D32]' 
                        : 'border-[#E5E5E5]'
                    }`}>
                      {propertyData.type === type.id && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Location & Price */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  Locatie & Prijs
                </h1>
                <p className="text-[#9E9E9E]">
                  Waar staat je woning en wat is de huurprijs?
                </p>
              </div>

              {/* Address Autocomplete */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Search className="w-4 h-4 text-[#2E7D32]" />
                  Adres zoeken
                </label>
                <div className="relative">
                  <Input
                    value={addressQuery}
                    onChange={(e) => {
                      setAddressQuery(e.target.value);
                      searchAddress(e.target.value);
                    }}
                    placeholder="Typ postcode of adres met huisnummer..."
                    className="h-12 rounded-xl border-[#E5E5E5] pr-10"
                  />
                  {loadingAddress && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E] animate-spin" />
                  )}
                </div>
                {addressSuggestions.length > 0 && (
                  <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-lg overflow-hidden">
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        onClick={() => selectAddress(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-[#F7F7F7] border-b border-[#E5E5E5] last:border-b-0 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-[#2A2A2A]">{suggestion.display}</p>
                            {suggestion.postcode && (
                              <p className="text-xs text-[#9E9E9E]">{suggestion.postcode} {suggestion.city}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-[#9E9E9E]">Typ een postcode of straatnaam met huisnummer - werkt voor heel Nederland</p>
              </div>

              {/* City - Manual input or auto-filled from address search */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#2E7D32]" />
                  Stad {propertyData.city && <span className="text-[#2E7D32]">✓</span>}
                </label>
                <Input
                  placeholder="Wordt automatisch ingevuld of typ handmatig"
                  className="h-12 rounded-xl border-[#E5E5E5]"
                  value={propertyData.city}
                  onChange={(e) => setPropertyData(prev => ({ ...prev, city: e.target.value }))}
                />
                <p className="text-xs text-[#9E9E9E]">Automatisch ingevuld via adres zoeken of vul handmatig in</p>
              </div>

              {/* Price */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Euro className="w-4 h-4 text-[#2E7D32]" />
                  Huurprijs per maand
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
                  <Input
                    type="number"
                    placeholder="750"
                    className="h-14 rounded-xl border-[#E5E5E5] pl-10 text-xl font-bold"
                    value={propertyData.price}
                    onChange={(e) => setPropertyData(prev => ({ ...prev, price: e.target.value }))}
                  />
                </div>
              </div>

              {/* Size */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-[#2E7D32]" />
                  Oppervlakte (m²) *
                </label>
                <Input
                  type="number"
                  placeholder="25"
                  className="h-12 rounded-xl border-[#E5E5E5]"
                  value={propertyData.size}
                  onChange={(e) => setPropertyData(prev => ({ ...prev, size: e.target.value }))}
                  required
                />
                <p className="text-xs text-[#9E9E9E]">Verplicht veld</p>
              </div>

              {/* Deposit/Borg */}
              <div className="space-y-2">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Euro className="w-4 h-4 text-[#9C27B0]" />
                  {language === 'nl' ? 'Borg (optioneel)' : 'Deposit (optional)'}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
                  <Input
                    type="number"
                    placeholder="1000"
                    className="h-12 rounded-xl border-[#E5E5E5] pl-10"
                    value={propertyData.deposit}
                    onChange={(e) => setPropertyData(prev => ({ ...prev, deposit: e.target.value }))}
                  />
                </div>
                <p className="text-xs text-[#9E9E9E]">
                  {language === 'nl' ? 'Meestal 1-2 maanden huur' : 'Usually 1-2 months rent'}
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Furnishing */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  Inrichting
                </h1>
                <p className="text-[#9E9E9E]">
                  Is je woning ingericht?
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setPropertyData(prev => ({ ...prev, furnished: true, semifurnished: false }))}
                  className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                    propertyData.furnished === true && !propertyData.semifurnished
                      ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                      : 'border-[#E5E5E5] bg-white'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      propertyData.furnished === true && !propertyData.semifurnished ? 'bg-[#2E7D32] text-white' : 'bg-[#F7F7F7] text-[#9E9E9E]'
                    }`}>
                      <Sofa className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2A2A2A] text-lg">Gemeubileerd</h3>
                      <p className="text-sm text-[#9E9E9E]">Inclusief bed, bureau, kast, etc.</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPropertyData(prev => ({ ...prev, furnished: false, semifurnished: true }))}
                  className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                    propertyData.semifurnished === true
                      ? 'border-[#1F3A5F] bg-[#1F3A5F]/5'
                      : 'border-[#E5E5E5] bg-white'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      propertyData.semifurnished === true ? 'bg-[#1F3A5F] text-white' : 'bg-[#F7F7F7] text-[#9E9E9E]'
                    }`}>
                      <Building2 className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2A2A2A] text-lg">Gestoffeerd</h3>
                      <p className="text-sm text-[#9E9E9E]">Vloerbedekking, gordijnen, keukenapparatuur</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPropertyData(prev => ({ ...prev, furnished: false, semifurnished: false }))}
                  className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                    propertyData.furnished === false && !propertyData.semifurnished
                      ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                      : 'border-[#E5E5E5] bg-white'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      propertyData.furnished === false && !propertyData.semifurnished ? 'bg-[#2E7D32] text-white' : 'bg-[#F7F7F7] text-[#9E9E9E]'
                    }`}>
                      <Home className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2A2A2A] text-lg">Leeg / Kaal</h3>
                      <p className="text-sm text-[#9E9E9E]">Huurder brengt alles zelf mee</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Available from */}
              <div className="space-y-2 mt-6">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#2E7D32]" />
                  Beschikbaar vanaf *
                </label>
                <Input
                  type="date"
                  className="h-12 rounded-xl border-[#E5E5E5]"
                  value={propertyData.available}
                  onChange={(e) => setPropertyData(prev => ({ ...prev, available: e.target.value }))}
                  required
                />
                <p className="text-xs text-[#C04A4A]">Verplicht veld</p>
              </div>

              {/* Available until */}
              <div className="space-y-2 mt-4">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#9C27B0]" />
                  Beschikbaar tot *
                </label>
                <div className="space-y-2">
                  <button
                    onClick={() => setPropertyData(prev => ({ ...prev, availableUntil: 'indefinite' }))}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      propertyData.availableUntil === 'indefinite'
                        ? 'border-[#9C27B0] bg-[#9C27B0]/5'
                        : 'border-[#E5E5E5] hover:border-[#9E9E9E]'
                    }`}
                  >
                    <span className="text-xl">♾️</span>
                    <span className="font-medium text-[#2A2A2A]">Onbepaalde tijd</span>
                  </button>
                  <Input
                    type="date"
                    className={`h-12 rounded-xl border-[#E5E5E5] ${propertyData.availableUntil === 'indefinite' ? 'opacity-50' : ''}`}
                    value={propertyData.availableUntil === 'indefinite' ? '' : (propertyData.availableUntil || '')}
                    onChange={(e) => setPropertyData(prev => ({ ...prev, availableUntil: e.target.value }))}
                    placeholder="Of selecteer een datum"
                  />
                </div>
              </div>

              {/* GWL - Utilities Section */}
              <div className="space-y-2 mt-6">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Euro className="w-4 h-4 text-[#FF9800]" />
                  Huurprijs inclusief of exclusief GWL? *
                </label>
                <p className="text-xs text-[#9E9E9E] mb-3">
                  GWL = Gas, Water en Licht (nutsvoorzieningen)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPropertyData(prev => ({ ...prev, utilitiesIncluded: true, utilitiesCost: '' }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      propertyData.utilitiesIncluded === true
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-2xl mb-2 block">✅</span>
                    <span className="font-bold text-[#2A2A2A]">Inclusief GWL</span>
                    <p className="text-xs text-[#9E9E9E] mt-1">Alles in de huurprijs</p>
                  </button>
                  <button
                    onClick={() => setPropertyData(prev => ({ ...prev, utilitiesIncluded: false }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      propertyData.utilitiesIncluded === false
                        ? 'border-[#FF9800] bg-[#FF9800]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#FF9800]/50'
                    }`}
                  >
                    <span className="text-2xl mb-2 block">➕</span>
                    <span className="font-bold text-[#2A2A2A]">Exclusief GWL</span>
                    <p className="text-xs text-[#9E9E9E] mt-1">GWL komt erbij</p>
                  </button>
                </div>
              </div>

              {/* GWL Cost - Only show if excluded */}
              {propertyData.utilitiesIncluded === false && (
                <div className="space-y-2 mt-4 p-4 bg-[#FF9800]/10 rounded-xl border border-[#FF9800]/30">
                  <label className="text-sm font-bold text-[#2A2A2A]">
                    Geschatte GWL kosten per maand (optioneel)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
                    <Input
                      type="number"
                      placeholder="100"
                      className="h-12 rounded-xl border-[#E5E5E5] pl-10"
                      value={propertyData.utilitiesCost}
                      onChange={(e) => setPropertyData(prev => ({ ...prev, utilitiesCost: e.target.value }))}
                    />
                  </div>
                  <p className="text-xs text-[#666]">Laat leeg als onbekend - huurder ziet "GWL exclusief"</p>
                </div>
              )}

              {/* Service Costs - Optional */}
              <div className="space-y-2 mt-4">
                <label className="text-sm font-bold text-[#2A2A2A] flex items-center gap-2">
                  <Euro className="w-4 h-4 text-[#9E9E9E]" />
                  Extra servicekosten per maand (optioneel)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] font-bold">€</span>
                  <Input
                    type="number"
                    placeholder="0"
                    className="h-12 rounded-xl border-[#E5E5E5] pl-10"
                    value={propertyData.serviceCost}
                    onChange={(e) => setPropertyData(prev => ({ ...prev, serviceCost: e.target.value }))}
                  />
                </div>
                <p className="text-xs text-[#9E9E9E]">Bijv. schoonmaak gemeenschappelijke ruimtes, internet, etc.</p>
              </div>
            </div>
          )}

          {/* Step 6: Property Features - NEW */}
          {step === 6 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  {language === 'nl' ? 'Woning Kenmerken' : 'Property Features'}
                </h1>
                <p className="text-[#9E9E9E]">
                  {language === 'nl' ? 'Selecteer alle kenmerken die van toepassing zijn' : 'Select all features that apply'}
                </p>
              </div>

              {/* Outdoor & Layout */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">🏡</span>
                  {language === 'nl' ? 'Oppervlakte & Indeling' : 'Surface & Layout'}
                </h3>
                <div className="space-y-4">
                  {/* Balkon */}
                  <div className="flex items-center justify-between">
                    <label className="text-[#2A2A2A]">{language === 'nl' ? 'Balkon/terras' : 'Balcony/terrace'}</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, balcony: true }))}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          propertyFeatures.balcony === true ? 'border-[#2E7D32] bg-[#2E7D32] text-white' : 'border-[#E5E5E5] text-[#666]'
                        }`}
                      >
                        Ja
                      </button>
                      <button
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, balcony: false, balconySize: '' }))}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          propertyFeatures.balcony === false ? 'border-red-400 bg-red-50 text-red-600' : 'border-[#E5E5E5] text-[#666]'
                        }`}
                      >
                        Nee
                      </button>
                      {propertyFeatures.balcony === true && (
                        <Input
                          type="number"
                          placeholder="m²"
                          className="w-20 h-10 text-center"
                          value={propertyFeatures.balconySize}
                          onChange={(e) => setPropertyFeatures(prev => ({ ...prev, balconySize: e.target.value }))}
                        />
                      )}
                    </div>
                  </div>
                  {/* Tuin */}
                  <div className="flex items-center justify-between">
                    <label className="text-[#2A2A2A]">{language === 'nl' ? 'Tuin' : 'Garden'}</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, garden: true }))}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          propertyFeatures.garden === true ? 'border-[#2E7D32] bg-[#2E7D32] text-white' : 'border-[#E5E5E5] text-[#666]'
                        }`}
                      >
                        Ja
                      </button>
                      <button
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, garden: false, gardenSize: '' }))}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          propertyFeatures.garden === false ? 'border-red-400 bg-red-50 text-red-600' : 'border-[#E5E5E5] text-[#666]'
                        }`}
                      >
                        Nee
                      </button>
                      {propertyFeatures.garden === true && (
                        <Input
                          type="number"
                          placeholder="m²"
                          className="w-20 h-10 text-center"
                          value={propertyFeatures.gardenSize}
                          onChange={(e) => setPropertyFeatures(prev => ({ ...prev, gardenSize: e.target.value }))}
                        />
                      )}
                    </div>
                  </div>
                  {/* Berging */}
                  <div className="flex items-center justify-between">
                    <label className="text-[#2A2A2A]">{language === 'nl' ? 'Berging/schuur' : 'Storage/shed'}</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, storage: true }))}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          propertyFeatures.storage === true ? 'border-[#2E7D32] bg-[#2E7D32] text-white' : 'border-[#E5E5E5] text-[#666]'
                        }`}
                      >
                        Ja
                      </button>
                      <button
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, storage: false }))}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          propertyFeatures.storage === false ? 'border-red-400 bg-red-50 text-red-600' : 'border-[#E5E5E5] text-[#666]'
                        }`}
                      >
                        Nee
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shared or Private */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">🚿</span>
                  {language === 'nl' ? 'Gedeeld of Eigen' : 'Shared or Private'}
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'kitchen', nl: 'Keuken', en: 'Kitchen' },
                    { key: 'shower', nl: 'Douche', en: 'Shower' },
                    { key: 'toilet', nl: 'Toilet', en: 'Toilet' },
                    { key: 'livingroom', nl: 'Woonkamer', en: 'Living room' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <label className="text-[#2A2A2A]">{language === 'nl' ? item.nl : item.en}</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPropertyFeatures(prev => ({ ...prev, [item.key]: 'eigen' }))}
                          className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                            propertyFeatures[item.key] === 'eigen' ? 'border-[#2E7D32] bg-[#2E7D32] text-white' : 'border-[#E5E5E5]'
                          }`}
                        >
                          {language === 'nl' ? 'Eigen' : 'Private'}
                        </button>
                        <button
                          onClick={() => setPropertyFeatures(prev => ({ ...prev, [item.key]: 'gedeeld' }))}
                          className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                            propertyFeatures[item.key] === 'gedeeld' ? 'border-[#FF9800] bg-[#FF9800] text-white' : 'border-[#E5E5E5]'
                          }`}
                        >
                          {language === 'nl' ? 'Gedeeld' : 'Shared'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Energy */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">⚡</span>
                  {language === 'nl' ? 'Energie' : 'Energy'}
                </h3>
                <div className="space-y-4">
                  {/* Energielabel */}
                  <div className="flex items-center justify-between">
                    <label className="text-[#2A2A2A]">{language === 'nl' ? 'Energielabel' : 'Energy label'}</label>
                    <select
                      value={propertyFeatures.energyLabel}
                      onChange={(e) => setPropertyFeatures(prev => ({ ...prev, energyLabel: e.target.value }))}
                      className="px-3 py-2 rounded-lg border-2 border-[#E5E5E5] bg-white"
                    >
                      <option value="">{language === 'nl' ? 'Selecteer...' : 'Select...'}</option>
                      {['A++++', 'A+++', 'A++', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'].map(label => (
                        <option key={label} value={label}>{label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Zonnepanelen */}
                  <div className="flex items-center justify-between">
                    <label className="text-[#2A2A2A]">{language === 'nl' ? 'Zonnepanelen' : 'Solar panels'}</label>
                    <button
                      onClick={() => setPropertyFeatures(prev => ({ ...prev, solarPanels: !prev.solarPanels }))}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        propertyFeatures.solarPanels ? 'border-[#2E7D32] bg-[#2E7D32] text-white' : 'border-[#E5E5E5]'
                      }`}
                    >
                      {propertyFeatures.solarPanels ? 'Ja' : 'Nee'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">🏠</span>
                  {language === 'nl' ? 'Voorzieningen' : 'Amenities'}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'elevator', nl: 'Lift aanwezig', en: 'Elevator' },
                    { key: 'washingConnection', nl: 'Wasmachine aansluiting', en: 'Washing connection' },
                    { key: 'dryerConnection', nl: 'Droger aansluiting', en: 'Dryer connection' },
                    { key: 'dishwasher', nl: 'Vaatwasser', en: 'Dishwasher' },
                    { key: 'internetIncluded', nl: 'Internet inbegrepen', en: 'Internet included' },
                    { key: 'smokingAllowed', nl: 'Roken toegestaan', en: 'Smoking allowed' },
                  ].map(item => (
                    <button
                      key={item.key}
                      onClick={() => setPropertyFeatures(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      className={`px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${
                        propertyFeatures[item.key] ? 'border-[#2E7D32] bg-[#2E7D32]/10 text-[#2E7D32]' : 'border-[#E5E5E5] text-[#666]'
                      }`}
                    >
                      {propertyFeatures[item.key] ? '✓ ' : ''}{language === 'nl' ? item.nl : item.en}
                    </button>
                  ))}
                </div>
                {/* Keuken type */}
                <div className="mt-4 flex items-center justify-between">
                  <label className="text-[#2A2A2A]">{language === 'nl' ? 'Keuken type' : 'Kitchen type'}</label>
                  <div className="flex gap-2">
                    {['luxe', 'standaard', 'basis'].map(type => (
                      <button
                        key={type}
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, kitchenType: type }))}
                        className={`px-3 py-2 rounded-lg border-2 text-sm capitalize transition-all ${
                          propertyFeatures.kitchenType === type ? 'border-[#2E7D32] bg-[#2E7D32] text-white' : 'border-[#E5E5E5]'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bathroom */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">🛁</span>
                  {language === 'nl' ? 'Badkamer' : 'Bathroom'}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'bathtub', nl: 'Bad', en: 'Bathtub' },
                    { key: 'showerBathroom', nl: 'Douche', en: 'Shower' },
                    { key: 'sink', nl: 'Wastafel', en: 'Sink' },
                    { key: 'toiletInBathroom', nl: 'Toilet in badkamer', en: 'Toilet in bathroom' },
                  ].map(item => (
                    <button
                      key={item.key}
                      onClick={() => setPropertyFeatures(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      className={`px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${
                        propertyFeatures[item.key] ? 'border-[#2E7D32] bg-[#2E7D32]/10 text-[#2E7D32]' : 'border-[#E5E5E5] text-[#666]'
                      }`}
                    >
                      {propertyFeatures[item.key] ? '✓ ' : ''}{language === 'nl' ? item.nl : item.en}
                    </button>
                  ))}
                </div>
              </div>

              {/* Parking */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">🚗</span>
                  {language === 'nl' ? 'Parkeren' : 'Parking'}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'parkingIncluded', nl: 'Parkeerplaats inclusief', en: 'Parking included' },
                    { key: 'parkingAvailable', nl: 'Parkeerplaats (betaald)', en: 'Parking (paid)' },
                    { key: 'parkingPermitNeeded', nl: 'Parkeervergunning nodig', en: 'Permit needed' },
                    { key: 'noParking', nl: 'Geen parkeermogelijkheid', en: 'No parking' },
                  ].map(item => (
                    <button
                      key={item.key}
                      onClick={() => setPropertyFeatures(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      className={`px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${
                        propertyFeatures[item.key] ? 'border-[#2E7D32] bg-[#2E7D32]/10 text-[#2E7D32]' : 'border-[#E5E5E5] text-[#666]'
                      }`}
                    >
                      {propertyFeatures[item.key] ? '✓ ' : ''}{language === 'nl' ? item.nl : item.en}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">📍</span>
                  {language === 'nl' ? 'Buurt & Locatie' : 'Neighborhood & Location'}
                </h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { key: 'publicTransport', nl: 'Goede OV verbinding', en: 'Good public transport' },
                    { key: 'supermarketNearby', nl: 'Supermarkt dichtbij', en: 'Supermarket nearby' },
                    { key: 'schoolNearby', nl: 'School op loopafstand', en: 'School nearby' },
                  ].map(item => (
                    <button
                      key={item.key}
                      onClick={() => setPropertyFeatures(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      className={`px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${
                        propertyFeatures[item.key] ? 'border-[#2E7D32] bg-[#2E7D32]/10 text-[#2E7D32]' : 'border-[#E5E5E5] text-[#666]'
                      }`}
                    >
                      {propertyFeatures[item.key] ? '✓ ' : ''}{language === 'nl' ? item.nl : item.en}
                    </button>
                  ))}
                </div>
                {/* Neighborhood type */}
                <div className="flex items-center justify-between">
                  <label className="text-[#2A2A2A]">{language === 'nl' ? 'Woonwijk type' : 'Neighborhood type'}</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'rustig', nl: 'Rustig', en: 'Quiet' },
                      { id: 'levendig', nl: 'Levendig', en: 'Lively' },
                      { id: 'centrum', nl: 'Centrum', en: 'Center' },
                    ].map(type => (
                      <button
                        key={type.id}
                        onClick={() => setPropertyFeatures(prev => ({ ...prev, neighborhoodType: type.id }))}
                        className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                          propertyFeatures.neighborhoodType === type.id ? 'border-[#2E7D32] bg-[#2E7D32] text-white' : 'border-[#E5E5E5]'
                        }`}
                      >
                        {language === 'nl' ? type.nl : type.en}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Extra Features */}
              <div className="bg-white rounded-2xl p-6 border border-[#E5E5E5]">
                <h3 className="font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                  <span className="text-xl">✨</span>
                  {language === 'nl' ? 'Extra Features' : 'Extra Features'}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'airco', nl: 'Airco', en: 'Air conditioning' },
                    { key: 'smartHome', nl: 'Smart home', en: 'Smart home' },
                    { key: 'alarmSystem', nl: 'Alarm systeem', en: 'Alarm system' },
                    { key: 'videoIntercom', nl: 'Video intercom', en: 'Video intercom' },
                    { key: 'bikeStorage', nl: 'Fietsenberging', en: 'Bike storage' },
                  ].map(item => (
                    <button
                      key={item.key}
                      onClick={() => setPropertyFeatures(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      className={`px-3 py-3 rounded-xl border-2 text-sm text-left transition-all ${
                        propertyFeatures[item.key] ? 'border-[#2E7D32] bg-[#2E7D32]/10 text-[#2E7D32]' : 'border-[#E5E5E5] text-[#666]'
                      }`}
                    >
                      {propertyFeatures[item.key] ? '✓ ' : ''}{language === 'nl' ? item.nl : item.en}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Photos */}
          {step === 7 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  Foto's
                </h1>
                <p className="text-[#9E9E9E]">
                  Goede foto's krijgen 4x meer reacties
                </p>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                multiple
                className="hidden"
              />

              {uploadedImages.length === 0 ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-[#2E7D32] bg-[#2E7D32]/5 flex flex-col items-center justify-center gap-4 hover:bg-[#2E7D32]/10 transition-colors"
                >
                  {isUploading ? (
                    <Loader2 className="w-12 h-12 text-[#2E7D32] animate-spin" />
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center">
                        <ImagePlus className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-[#2A2A2A]">Tik om foto's toe te voegen</p>
                        <p className="text-sm text-[#9E9E9E]">JPG, PNG (max 4MB)</p>
                      </div>
                    </>
                  )}
                </button>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {uploadedImages.map((url, index) => (
                      <div key={index} className="relative aspect-square">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                          alt={`Foto ${index + 1}`}
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <button
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-2 left-2 bg-[#2E7D32] text-white text-xs px-2 py-1 rounded-lg font-medium">
                            Hoofdfoto
                          </span>
                        )}
                      </div>
                    ))}
                    
                    {uploadedImages.length < 6 && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="aspect-square rounded-xl border-2 border-dashed border-[#E5E5E5] flex flex-col items-center justify-center gap-2 hover:border-[#2E7D32] transition-colors"
                      >
                        <Upload className="w-6 h-6 text-[#9E9E9E]" />
                        <span className="text-xs text-[#9E9E9E]">Meer</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 8: Title & Description */}
          {step === 8 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h1 className="text-2xl sm:text-3xl font-black text-[#2A2A2A] mb-2">
                  Bijna klaar!
                </h1>
                <p className="text-[#9E9E9E]">
                  Geef je woning een pakkende titel
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-[#2A2A2A]">Titel</label>
                <Input
                  placeholder="bijv. Ruime kamer in gezellig studentenhuis"
                  className="h-12 rounded-xl border-[#E5E5E5]"
                  value={propertyData.title}
                  onChange={(e) => setPropertyData(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-[#2A2A2A]">Beschrijving</label>
                <Textarea
                  placeholder="Beschrijf je woning. Wat maakt het bijzonder? Hoe is de sfeer?"
                  className="min-h-[150px] rounded-xl border-[#E5E5E5]"
                  value={propertyData.description}
                  onChange={(e) => setPropertyData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              {/* Summary */}
              <div className="bg-white rounded-2xl p-4 border border-[#E5E5E5] mt-6">
                <h3 className="font-bold text-[#2A2A2A] mb-4">📋 Samenvatting van je advertentie</h3>
                
                {/* Basic Info */}
                <div className="space-y-2 text-sm pb-3 border-b border-[#E5E5E5]">
                  <h4 className="font-semibold text-[#2E7D32]">Basis</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Type</span>
                      <span className="font-medium text-[#2A2A2A] capitalize">{propertyData.type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Locatie</span>
                      <span className="font-medium text-[#2A2A2A]">{propertyData.city}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Oppervlakte</span>
                      <span className="font-medium text-[#2A2A2A]">{propertyData.size} m²</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Foto's</span>
                      <span className="font-medium text-[#2A2A2A]">{uploadedImages.length} stuks</span>
                    </div>
                  </div>
                </div>

                {/* Costs */}
                <div className="space-y-2 text-sm py-3 border-b border-[#E5E5E5]">
                  <h4 className="font-semibold text-[#2E7D32]">Kosten</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Huur</span>
                      <span className="font-bold text-[#2E7D32]">€{propertyData.price}/mnd</span>
                    </div>
                    {propertyData.deposit && (
                      <div className="flex justify-between">
                        <span className="text-[#9E9E9E]">Borg</span>
                        <span className="font-medium text-[#2A2A2A]">€{propertyData.deposit}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">GWL</span>
                      <span className={`font-medium ${propertyData.utilitiesIncluded ? 'text-[#2E7D32]' : 'text-orange-600'}`}>
                        {propertyData.utilitiesIncluded ? 'Inclusief' : propertyData.utilitiesCost ? `Excl. (€${propertyData.utilitiesCost})` : 'Exclusief'}
                      </span>
                    </div>
                    {propertyData.serviceCost && (
                      <div className="flex justify-between">
                        <span className="text-[#9E9E9E]">Servicekosten</span>
                        <span className="font-medium text-[#2A2A2A]">€{propertyData.serviceCost}/mnd</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Availability */}
                <div className="space-y-2 text-sm py-3 border-b border-[#E5E5E5]">
                  <h4 className="font-semibold text-[#2E7D32]">Beschikbaarheid</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Vanaf</span>
                      <span className="font-medium text-[#2A2A2A]">
                        {propertyData.available ? new Date(propertyData.available).toLocaleDateString('nl-NL') : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Tot</span>
                      <span className="font-medium text-[#2A2A2A]">
                        {propertyData.availableUntil === 'indefinite' ? 'Onbepaald' : new Date(propertyData.availableUntil).toLocaleDateString('nl-NL')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Inrichting</span>
                      <span className="font-medium text-[#2A2A2A]">
                        {propertyData.furnished ? 'Gemeubileerd' : propertyData.semifurnished ? 'Gestoffeerd' : 'Ongemeubileerd'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Features Summary */}
                <div className="space-y-2 text-sm py-3 border-b border-[#E5E5E5]">
                  <h4 className="font-semibold text-[#2E7D32]">Kenmerken</h4>
                  <div className="flex flex-wrap gap-1">
                    {propertyFeatures.balcony && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Balkon</span>}
                    {propertyFeatures.garden && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Tuin</span>}
                    {propertyFeatures.terrace && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Terras</span>}
                    {propertyFeatures.roofTerrace && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Dakterras</span>}
                    {propertyFeatures.elevator && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">Lift</span>}
                    {propertyFeatures.parkingIncluded && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">Parkeren incl.</span>}
                    {propertyFeatures.bikeStorage && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">Fietsenstalling</span>}
                    {propertyFeatures.storage && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">Berging</span>}
                    {propertyFeatures.internetIncluded && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">Internet incl.</span>}
                    {propertyFeatures.tvIncluded && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">TV incl.</span>}
                    {propertyFeatures.airco && <span className="bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded text-xs">Airco</span>}
                    {propertyFeatures.floorHeating && <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs">Vloerverwarming</span>}
                    {propertyFeatures.dishwasher && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">Vaatwasser</span>}
                    {propertyFeatures.washingMachine && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">Wasmachine</span>}
                    {propertyFeatures.dryer && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">Droger</span>}
                    {propertyFeatures.kitchen === 'eigen' && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Eigen keuken</span>}
                    {propertyFeatures.kitchen === 'gedeeld' && <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs">Gedeelde keuken</span>}
                    {propertyFeatures.shower === 'eigen' && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Eigen badkamer</span>}
                    {propertyFeatures.shower === 'gedeeld' && <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs">Gedeelde badkamer</span>}
                    {propertyFeatures.toilet === 'eigen' && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">Eigen toilet</span>}
                    {propertyFeatures.toilet === 'gedeeld' && <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs">Gedeeld toilet</span>}
                    {propertyFeatures.energyLabel && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs">Energie: {propertyFeatures.energyLabel}</span>}
                    {propertyFeatures.publicTransport && <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">OV dichtbij</span>}
                    {propertyFeatures.supermarketNearby && <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">Supermarkt dichtbij</span>}
                    {propertyFeatures.schoolNearby && <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">School dichtbij</span>}
                    {!Object.values(propertyFeatures).some(v => v) && <span className="text-[#9E9E9E]">Geen kenmerken geselecteerd</span>}
                  </div>
                </div>

                {/* Additional Rules */}
                <div className="space-y-2 text-sm py-3 border-b border-[#E5E5E5]">
                  <h4 className="font-semibold text-[#2E7D32]">Regels & Toestemmingen</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Huisdieren</span>
                      <span className={`font-medium ${petsAllowed ? 'text-green-600' : petsAllowed === false ? 'text-red-500' : 'text-orange-500'}`}>
                        {petsAllowed ? '✓ Toegestaan' : petsAllowed === false ? '✗ Niet toegestaan' : '? Bespreekbaar'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Inschrijving</span>
                      <span className={`font-medium ${propertyData.registrationAllowed ? 'text-green-600' : 'text-red-500'}`}>
                        {propertyData.registrationAllowed ? '✓ Mogelijk' : '✗ Niet mogelijk'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Max. personen</span>
                      <span className="font-medium text-[#2A2A2A]">{maxPersons || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Min. inkomen</span>
                      <span className="font-medium text-[#2A2A2A]">{propertyData.minIncome ? `${propertyData.minIncome}x huur` : '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Preferences */}
                <div className="space-y-2 text-sm pt-3">
                  <h4 className="font-semibold text-[#2E7D32]">Voorkeuren</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Huurders</span>
                      <span className="font-medium text-[#2A2A2A]">
                        {preferredTenants.includes('any') ? 'Iedereen' : `${preferredTenants.length} type(s)`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Huisdieren</span>
                      <span className="font-medium text-[#2A2A2A]">{petsAllowed ? 'Ja' : petsAllowed === false ? 'Nee' : 'Bespreekbaar'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9E9E9E]">Min. huurtermijn</span>
                      <span className="font-medium text-[#2A2A2A]">
                        {RENTAL_DURATIONS.find(d => d.id === minRentalDuration)?.label || 'Flexibel'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Bottom Action */}
      <div className="absolute bottom-0 left-0 right-0 z-30 bg-[#F7F7F7] border-t border-[#E5E5E5] p-4">
        <div className="max-w-xl mx-auto">
          {step < TOTAL_STEPS ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold text-lg disabled:opacity-50"
            >
              Volgende
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold text-lg disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Bezig...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Woning plaatsen & swipen
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandlordOnboardingPage;
